// src/services/azureTeamService.js
const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "team",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const generateTeamId = () =>
  `team_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const UUID_V4_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Kanonische Personen-ID. Akzeptiert externe UUIDs (z. B. von AbaNinja) nur,
// wenn sie dem v4-Format entsprechen; andernfalls wird intern eine neue UUID
// generiert. Verhindert Wiedereinschleusen von Nummer/Email/Test-Codes.
const resolveEmployeeUuid = (provided) => {
  if (typeof provided === "string" && UUID_V4_RE.test(provided)) {
    return provided;
  }
  return crypto.randomUUID();
};

export const TEAM_CATEGORIES = ["GL", "PL", "Architekt:in", "Bauleiter:in", "Praktikant:in", "Freelancer", "El Gato Muerto"];

// System roles mapping from Team categories
export const SYSTEM_ROLES = {
  ADMIN: "MF-Admin",
  PL: "MF-PL",
  TEAM: "MF-Team",
  FREELANCER: "MF-Freelancer",
  // Externe Person: ausschliesslich Zugriff auf das El-Gato-Muerto-Tool
  ELGATO: "MF-ElGatoMuerto"
};

// Map Team table categories to system roles
export const CATEGORY_TO_ROLE = {
  "GL": SYSTEM_ROLES.ADMIN,        // Geschäftsleitung -> Admin
  "PL": SYSTEM_ROLES.PL,           // Projektleiter -> PL
  "Architekt:in": SYSTEM_ROLES.TEAM,
  "Bauleiter:in": SYSTEM_ROLES.TEAM,
  "Praktikant:in": SYSTEM_ROLES.TEAM,
  "Freelancer": SYSTEM_ROLES.FREELANCER,
  "El Gato Muerto": SYSTEM_ROLES.ELGATO
};

// Get system role from team category
export const getRoleFromCategory = (kategorie) => {
  return CATEGORY_TO_ROLE[kategorie] || SYSTEM_ROLES.TEAM;
};

export const createTeamMember = async (memberData) => {
  if (!memberData?.name?.trim()) {
    throw new Error("createTeamMember: Pflichtfeld 'name' fehlt.");
  }
  if (!memberData?.kategorie) {
    throw new Error("createTeamMember: Pflichtfeld 'kategorie' fehlt.");
  }

  const teamId = generateTeamId();
  const now = new Date().toISOString();
  const employeeUuid = resolveEmployeeUuid(memberData.employeeUuid);

  // For freelancers, auto-assign the system role
  const isFreelancer = memberData.kategorie === "Freelancer";
  const rolle = isFreelancer ? SYSTEM_ROLES.FREELANCER : (memberData.rolle || "");

  const entity = {
    PartitionKey: "team",
    RowKey: teamId,
    EmployeeUuid: employeeUuid,
    Nummer: memberData.nummer || "",
    Name: memberData.name || "",
    Email: memberData.email || "", // Login email for access control
    InternalRate: memberData.internalRate || 0,
    Pensum: memberData.pensum !== undefined ? memberData.pensum : 100,
    Start: memberData.start || "",
    Ende: memberData.ende || "",
    Beschrieb: memberData.beschrieb || "",
    Public: memberData.public || false,
    ImTeam: memberData.imTeam !== undefined ? memberData.imTeam : true,
    Kategorie: memberData.kategorie || "",
    Geschlecht: memberData.geschlecht || "",
    Geburtsdatum: memberData.geburtsdatum || "",
    BildId: memberData.bildId || "",
    BildUrls: memberData.bildUrls ? JSON.stringify(memberData.bildUrls) : "",
    // Freelancer-specific fields
    Rolle: rolle,
    ZugeordneteProjekte: memberData.zugeordneteProjekte
      ? JSON.stringify(memberData.zugeordneteProjekte)
      : "[]",
    Kinder: memberData.kinder ? JSON.stringify(memberData.kinder) : "[]",
    NoAbaNinja: memberData.noAbaNinja || false,
    CreatedAt: now,
    UpdatedAt: now,
  };

  console.log("Creating team member:", entity);

  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Create failed:", response.status, errorText);
    throw new Error(`Create failed: ${response.status}`);
  }

  return { teamId, ...memberData, employeeUuid, createdAt: now, updatedAt: now };
};

export const getAllTeamMembers = async () => {
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}()?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    headers: { Accept: "application/json;odata=nometadata" },
  });

  if (!response.ok) {
    throw new Error(`Load failed: ${response.status}`);
  }

  const data = await response.json();

  return data.value.map((item) => ({
    teamId: item.RowKey,
    employeeUuid: item.EmployeeUuid || "",
    nummer: item.Nummer || "",
    name: item.Name || "",
    email: item.Email || "", // Login email for access control
    internalRate: item.InternalRate || 0,
    pensum: item.Pensum !== undefined ? item.Pensum : 100,
    start: item.Start || "",
    ende: item.Ende || "",
    beschrieb: item.Beschrieb || "",
    public: item.Public || false,
    imTeam: item.ImTeam !== undefined ? item.ImTeam : true,
    kategorie: item.Kategorie || "",
    geschlecht: item.Geschlecht || "",
    geburtsdatum: item.Geburtsdatum || "",
    bildId: item.BildId || "",
    bildUrls: item.BildUrls ? JSON.parse(item.BildUrls) : null,
    // Freelancer-specific fields
    rolle: item.Rolle || "",
    zugeordneteProjekte: item.ZugeordneteProjekte
      ? JSON.parse(item.ZugeordneteProjekte)
      : [],
    kinder: item.Kinder ? JSON.parse(item.Kinder) : [],
    noAbaNinja: item.NoAbaNinja || false,
    createdAt: item.CreatedAt,
    updatedAt: item.UpdatedAt,
  }));
};

export const getTeamMemberById = async (teamId) => {
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='team',RowKey='${encodeURIComponent(teamId)}')?${AZURE_CONFIG.sasToken}`;
  const response = await fetch(url, {
    headers: { Accept: "application/json;odata=nometadata" },
  });
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`getTeamMemberById failed: ${response.status}`);
  }
  const item = await response.json();
  return {
    teamId: item.RowKey,
    employeeUuid: item.EmployeeUuid || "",
    nummer: item.Nummer || "",
    name: item.Name || "",
    email: item.Email || "",
    kategorie: item.Kategorie || "",
  };
};

export const updateTeamMember = async (teamId, memberData) => {
  const now = new Date().toISOString();

  // For freelancers, auto-assign the system role
  const isFreelancer = memberData.kategorie === "Freelancer";
  const rolle = isFreelancer ? SYSTEM_ROLES.FREELANCER : (memberData.rolle || "");

  // EmployeeUuid ist nach der Migration immutable. Wenn ein Aufrufer keinen
  // gueltigen v4-Wert mitgibt, lesen wir den aktuellen Wert aus der Tabelle
  // anstatt etwas Neues zu generieren oder den Tabellen-Eintrag zu beschaedigen.
  let employeeUuid = memberData.employeeUuid;
  if (!UUID_V4_RE.test(employeeUuid || "")) {
    const existing = await getTeamMemberById(teamId);
    if (!existing || !UUID_V4_RE.test(existing.employeeUuid || "")) {
      throw new Error(
        `updateTeamMember: konnte EmployeeUuid fuer ${teamId} nicht ermitteln.`,
      );
    }
    employeeUuid = existing.employeeUuid;
  }

  const entity = {
    PartitionKey: "team",
    RowKey: teamId,
    EmployeeUuid: employeeUuid,
    Nummer: memberData.nummer || "",
    Name: memberData.name || "",
    Email: memberData.email || "", // Login email for access control
    InternalRate: memberData.internalRate || 0,
    Pensum: memberData.pensum !== undefined ? memberData.pensum : 100,
    Start: memberData.start || "",
    Ende: memberData.ende || "",
    Beschrieb: memberData.beschrieb || "",
    Public: memberData.public || false,
    ImTeam: memberData.imTeam !== undefined ? memberData.imTeam : true,
    Kategorie: memberData.kategorie || "",
    Geschlecht: memberData.geschlecht || "",
    Geburtsdatum: memberData.geburtsdatum || "",
    BildId: memberData.bildId || "",
    BildUrls: memberData.bildUrls ? JSON.stringify(memberData.bildUrls) : "",
    // Freelancer-specific fields
    Rolle: rolle,
    ZugeordneteProjekte: memberData.zugeordneteProjekte
      ? JSON.stringify(memberData.zugeordneteProjekte)
      : "[]",
    Kinder: memberData.kinder ? JSON.stringify(memberData.kinder) : "[]",
    NoAbaNinja: memberData.noAbaNinja || false,
    CreatedAt: memberData.createdAt || now,
    UpdatedAt: now,
  };

  console.log("Updating team member:", teamId, entity);

  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='team',RowKey='${teamId}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Update failed:", response.status, errorText);
    throw new Error(`Update failed: ${response.status}`);
  }

  return true;
};

export const deleteTeamMember = async (teamId) => {
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='team',RowKey='${teamId}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: { "If-Match": "*" },
  });

  if (!response.ok && response.status !== 404) {
    throw new Error(`Delete failed: ${response.status}`);
  }

  return true;
};

// Get assigned project codes for a freelancer (matched per Email).
export const getFreelancerAssignedProjects = async (email) => {
  const member = await getTeamMemberByEmail(email);
  if (!member || member.kategorie !== "Freelancer") return [];
  return member.zugeordneteProjekte || [];
};

// Get all freelancers
export const getAllFreelancers = async () => {
  try {
    const allMembers = await getAllTeamMembers();
    return allMembers.filter((member) => member.kategorie === "Freelancer");
  } catch (error) {
    console.error("Error getting freelancers:", error);
    return [];
  }
};

// Get any team member by email (for access control)
// Returns the team member with their category and assigned role
export const getTeamMemberByEmail = async (email) => {
  try {
    if (!email) return null;

    const allMembers = await getAllTeamMembers();
    const normalizedEmail = email.toLowerCase().trim();

    const member = allMembers.find(
      (m) => m.email?.toLowerCase().trim() === normalizedEmail,
    );

    if (member) {
      // Auto-assign role based on category
      const assignedRole = getRoleFromCategory(member.kategorie);
      return {
        ...member,
        assignedRole,
        isFreelancer: member.kategorie === "Freelancer"
      };
    }

    return null;
  } catch (error) {
    console.error("Error getting team member by email:", error);
    return null;
  }
};

// Check if a user is registered in the Team table
export const isUserRegistered = async (email) => {
  const member = await getTeamMemberByEmail(email);
  return member !== null;
};
